import { Box, Button, Card, MenuItem, Select, TextField, Typography, makeStyles } from '@material-ui/core'
import {
  BoardEditor,
  FlexHorizontal,
  IBoard,
  IBoardStyle,
  Spacer,
  createBoard,
  useToasts
} from '@vestaboard/installables'
import React, { useState } from 'react'

import { CreatePickInputV2 } from '../../../../__generated__/globalTypes'
import { GetPickV2_pick_PickV2 } from '../hooks/__generated__/GetPickV2'
import { useCreateMessage } from '../../message-v2'
import { useHistory } from 'react-router'
import { useListPickCategories } from '../hooks/useListPickCategories'

const useStyles = makeStyles({
  cardInner: {
    padding: 24
  }
})

interface IPickForm {
  pick?: GetPickV2_pick_PickV2
  initialDate?: string
  onSave: (input: CreatePickInputV2) => void
  onDelete?: () => void
}

export const PickForm = (props: IPickForm) => {
  const classes = useStyles()
  const [boardStyle, setBoardStyle] = useState<string>(
    props?.pick?.message?.cardSize === '15x3' ? 'note-black' : 'black'
  )
  const [boardValue, setBoardValue] = useState<IBoard>(
    (props?.pick?.message?.characters as IBoard) ||
      createBoard({
        rows: props?.pick?.message?.cardSize === '15x3' ? 3 : 6,
        columns: props?.pick?.message?.cardSize === '15x3' ? 15 : 22
      })
  )
  const [date, setDate] = useState<string | null>(props?.pick?.date || props.initialDate || new Date().toISOString().split('T')[0])
  const [order, setOrder] = useState<number>(props?.pick?.sortOrder || 0)
  const [categoryIds, setCategoryIds] = useState<string[]>(props?.pick?.categories?.map(category => category.id) || [])
  const { data: categoriesData } = useListPickCategories({
    input: {}
  })
  const { addToast } = useToasts()
  const history = useHistory()
  const [error, setError] = useState<string | null>(null)
  const [createMessageMutation] = useCreateMessage()

  const handleSave = async () => {
    if (!date) {
      setError('date')
      addToast('Date is required', {
        appearance: 'error'
      })
      return
    }

    if (!categoryIds.length) {
      setError('category')
      addToast('At least one category must be selected', {
        appearance: 'error'
      })
      return
    }

    const response = await createMessageMutation({
      variables: {
        input: {
          characters: boardValue
        }
      }
    })

    if (response.data?.createMessage.__typename !== 'MessageV2') {
      addToast(response.data?.createMessage.error || 'There was an error creating your message', {
        appearance: 'error'
      })
      return
    }

    props.onSave({
      messageId: response.data.createMessage.id,
      date,
      order,
      categoryIds
    })
  }

  return (
    <>
      <Card>
        <Box className={classes.cardInner}>
          <TextField
            value={date}
            onChange={e => {
              setDate(e.target.value)
            }}
            variant='outlined'
            label='Date'
            fullWidth
            type='date'
            error={error === 'date' && !date}
          />
          <Spacer size='large' />
          <TextField
            value={order}
            onChange={e => {
              const number = +e.target.value
              setOrder(number > 0 ? number : 0)
            }}
            variant='outlined'
            label='Sort Order'
            fullWidth
            type='number'
          />
          <Spacer size='large' />
          <Typography>Categories</Typography>
          <Select
            multiple
            multiline
            fullWidth
            value={categoryIds}
            variant='outlined'
            error={error === 'category' && !categoryIds.length}
            onChange={e => setCategoryIds(e.target.value as string[])}>
            {categoriesData?.listPickCategories.pickCategories.map(d => (
              <MenuItem key={d.id} value={d.id}>
                {d.title}
              </MenuItem>
            ))}
          </Select>
          <Spacer size='large' />
          <Typography>Size</Typography>
          <Select
            fullWidth
            value={boardStyle}
            variant='outlined'
            onChange={e => {
              const newStyle = e.target.value as string
              setBoardStyle(newStyle)
              setBoardValue(
                createBoard({
                  rows: newStyle === 'note-black' ? 3 : 6,
                  columns: newStyle === 'note-black' ? 15 : 22
                })
              )
            }}>
            {[
              {
                id: 'black',
                title: 'Flagship'
              },
              {
                id: 'note-black',
                title: 'Note'
              }
            ].map(d => (
              <MenuItem key={d.id} value={d.id}>
                {d.title}
              </MenuItem>
            ))}
          </Select>
          <Spacer size='large' />
          <FlexHorizontal>
            <Button
              onClick={() => {
                history.push('/picks')
              }}>
              Cancel
            </Button>
            <Box width={24} />
            <Button onClick={handleSave} variant='contained' color='primary' size='large'>
              Save
            </Button>
            <Box width={24} />
            {props.onDelete ? (
              <Button onClick={props.onDelete} variant='contained' color='secondary' size='large'>
                Delete
              </Button>
            ) : null}
          </FlexHorizontal>
        </Box>
      </Card>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Message</Typography>
      <Spacer size='large' />
      <Box style={{ backgroundColor: '#111111', padding: 24, borderRadius: 8 }}>
        <BoardEditor
          boardValue={boardValue}
          setBoardValue={setBoardValue}
          boardStyle={boardStyle as IBoardStyle}
          showFilledColor
        />
      </Box>
      <Spacer size='extraLarge' />
    </>
  )
}
